import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, TextField, Button, Chip, IconButton,
  Autocomplete, Popper, InputAdornment, FormControlLabel, Switch, CircularProgress,
  Menu, MenuItem, Select, Tooltip, Stack, Avatar, Dialog, DialogTitle, DialogContent, Popover,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import LinkIcon from "@mui/icons-material/Link";
import { useNavigate, useLocation } from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ShortTextIcon from "@mui/icons-material/ShortText";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import EmojiPicker from "emoji-picker-react";
import { toast } from "react-toastify";
import { Auth } from "../../contexts/AuthContext";
import UserService from "../../services/categoryService";
import { Content_Generation, Content_Refinement } from "../../services/ai_post_generation";
import { createPost, uploadMedia, createLinkedinExternalPost, createXExternalPost, getAllPlatforms, updatePostContent, scheduleExternalPost } from "../../services/postService";
import { employeeTrendingTopics, globalTrendingTopics } from "../../services/adminServices";
import { composePlatformContent, isPayoneerDomain, stripHashtagsFromText } from "../../shared/businessPageComposer";
import PostPreview from "./PostPreview";
import TeaLoadingModal from "../../components/teaLoadingModal/TeaLoadingModal";
import "./ComposePage.css";
import "../../components/Layout/Postcreation/Postcreation.css";

const LINKEDIN_LIMIT = 3000;
const TONE_OPTIONS = ["Professional", "Casual", "Inspirational", "Humorous", "Authoritative"];

const LINKEDIN_ICON = "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png";
const TWITTER_ICON = "https://abs.twimg.com/favicons/twitter.3.ico";

const PLATFORMS = [
  { key: "LINKEDIN", name: "Linkedin", icon: LINKEDIN_ICON },
  { key: "X", name: "Twitter / X", icon: TWITTER_ICON },
];

const ComposePage = ({ userProfile, editPostId = null, initialContent = "", onEditSuccess, onEditCancel }) => {
  const isEditMode = !!editPostId;
  const { categoriesData, businessPages } = Auth();
  const [personalConnections, setPersonalConnections] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  const [topic, setTopic] = useState("");
  const [content, setContent] = useState(initialContent);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const contentRef = useRef(null);

  const handleEmojiClickCompose = (emojiData) => {
    const emoji = emojiData?.emoji || "";
    if (!emoji) return;

    const ta = contentRef.current;
    const currentVal = content || "";

    if (ta) {
      const start = ta.selectionStart ?? currentVal.length;
      const end = ta.selectionEnd ?? currentVal.length;
      const before = currentVal.substring(0, start);
      const after = currentVal.substring(end);
      const newValue = before + emoji + after;
      setContent(newValue);

      requestAnimationFrame(() => {
        if (ta) {
          const newPos = start + emoji.length;
          ta.focus();
          ta.setSelectionRange(newPos, newPos);
        }
      });
    } else {
      setContent(currentVal + emoji);
    }
    setEmojiAnchor(null);
  };
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [categoryCreating, setCategoryCreating] = useState(false);
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [newCategoryDialogInput, setNewCategoryDialogInput] = useState("");
  const [featuredUrl, setFeaturedUrl] = useState("");
  const hasFeatured = categories.some(c => c.name?.toLowerCase() === "featured");
  const [allowEdit, setAllowEdit] = useState(false);
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTimeString = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(getTodayDateString);
  const [scheduleTime, setScheduleTime] = useState(getCurrentTimeString);

  // Platform toggles — off by default; only enabled if account is connected AND user turns on
  const [platformEnabled, setPlatformEnabled] = useState({ LINKEDIN: false, X: false });

  const [aiLoading, setAiLoading] = useState(false);
  const [refineLoading, setRefineLoading] = useState(false);
  const [showGeneratingPopup, setShowGeneratingPopup] = useState(false);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (aiLoading) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : "."));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [aiLoading]);
  const [toneAnchor, setToneAnchor] = useState(null);
  const [posting, setPosting] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [connectDialog, setConnectDialog] = useState(null); // platform name string
  const [trendsData, setTrendsData] = useState([]);
  const [orgTrendsData, setOrgTrendsData] = useState([]);
  const [personalTrendsData, setPersonalTrendsData] = useState([]);
  const [trendingTopicType, setTrendingTopicType] = useState("org");
  const [selectedTopic, setSelectedTopic] = useState("");

  useEffect(() => {
    setTrendsData(trendingTopicType === "org" ? orgTrendsData : personalTrendsData);
  }, [trendingTopicType, orgTrendsData, personalTrendsData]);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const [empRes, globalRes] = await Promise.all([
          employeeTrendingTopics().catch(() => null),
          globalTrendingTopics().catch(() => null),
        ]);
        const clean = (arr) => [...new Set(arr)]
          .map(t => typeof t === "string" ? t.replace(/^\[|\]$/g, "").replace(/^"|"$/g, "").trim() : t)
          .filter(Boolean);
        const empTopics = clean(empRes?.status ? (empRes.trendingTopics || []) : []);
        const globalTopics = clean(globalRes?.status ? (globalRes.trendingTopics || []) : []);
        setOrgTrendsData(globalTopics);
        setPersonalTrendsData(empTopics);
      } catch (_) { }
    };
    fetchTrends();
  }, []);

  const handleTopicChipClick = (t) => {
    setSelectedTopic(t);
    setTopic(t);
  };

  const charCount = content.length;
  const overLimit = charCount > LINKEDIN_LIMIT;
  const hasContent = content.trim().length > 0;
  const anyPlatformSelected = platformEnabled.LINKEDIN || platformEnabled.X;
  const isLoading = aiLoading || refineLoading || posting || draftSaving;

  useEffect(() => () => imageUrls.forEach(URL.revokeObjectURL), [imageUrls]);

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const response = await getAllPlatforms();
        if (response?.status === true) {
          const connected = {};
          (response?.socialMediaConnectionData || []).forEach(item => {
            if (item.status === "CONNECTED") connected[item.platform.toUpperCase()] = true;
          });
          setPersonalConnections(connected);
        }
      } catch (e) { }
    };
    fetchConnections();
  }, []);

  const isConnected = (key) => {
    const bpMatch = businessPages && Object.keys(businessPages).some(k => k.toUpperCase() === key || (key === "X" && k.toUpperCase() === "TWITTER"));
    const pcMatch = personalConnections && (
      personalConnections[key] ||
      (key === "X" && (personalConnections["TWITTER"] || personalConnections["TWITTER / X"]))
    );
    return !!(bpMatch || pcMatch);
  };

  // ── AI ───────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic first"); return; }
    try {
      setAiLoading(true);
      setShowGeneratingPopup(true);
      const xComp = composePlatformContent("", "x", businessPages);
      const res = await Content_Generation({ topic, xMaxCharacters: xComp.maxBaseCharacters || 200 });
      let genText = res?.contentGenerateResponseDTO?.generatedText;
      if (genText) {
        if (isPayoneerDomain(businessPages)) {
          genText = stripHashtagsFromText(genText);
        }
        setContent(genText);
      }
    } catch (err) { toast.error(err.message || "Generation failed"); }
    finally {
      setAiLoading(false);
      setShowGeneratingPopup(false);
    }
  };

  const handleRefine = async (action, tone) => {
    if (!content.trim()) { toast.error("Write content first"); return; }
    try {
      setRefineLoading(true);
      setShowGeneratingPopup(true);
      const res = await Content_Refinement({ content, action, tone });
      let refined = res?.refinedText || res?.contentGenerateResponseDTO?.generatedText;
      if (refined) {
        if (isPayoneerDomain(businessPages)) {
          refined = stripHashtagsFromText(refined);
        }
        setContent(refined);
      }
      else toast.info("No result returned — try again");
    } catch (err) { toast.error(err.message || "Refinement failed"); }
    finally {
      setRefineLoading(false);
      setShowGeneratingPopup(false);
    }
  };

  // ── Media ────────────────────────────────────────────────────────────
  const handleImageSelect = (e) => {
    const files = [...images, ...Array.from(e.target.files)].slice(0, 5);
    imageUrls.forEach(URL.revokeObjectURL);
    setImages(files);
    setImageUrls(files.map(f => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const removeImage = (i) => {
    URL.revokeObjectURL(imageUrls[i]);
    setImages(p => p.filter((_, j) => j !== i));
    setImageUrls(p => p.filter((_, j) => j !== i));
  };

  // ── Upload & post ────────────────────────────────────────────────────
  const uploadAllMedia = async () => {
    const allIds = [];
    try {
      // Upload images
      if (images.length > 0) {
        const fd = new FormData();
        images.forEach(f => fd.append("files", f));
        fd.append("mediaType", "image");
        const r = await uploadMedia(fd);
        const ids = r?.mediaUploadData?.imageIds || [];
        allIds.push(...ids);
      }
      // Upload videos
      if (videos.length > 0) {
        const fd = new FormData();
        videos.forEach(f => fd.append("files", f));
        fd.append("mediaType", "video");
        const r = await uploadMedia(fd);
        const ids = r?.mediaUploadData?.videoIds || r?.mediaUploadData?.imageIds || [];
        allIds.push(...ids);
      }
      // Upload documents / PDFs
      if (attachedFiles.length > 0) {
        const fd = new FormData();
        attachedFiles.forEach(f => fd.append("files", f));
        fd.append("mediaType", "document");
        const r = await uploadMedia(fd);
        const ids = r?.mediaUploadData?.documentIds || r?.mediaUploadData?.imageIds || [];
        allIds.push(...ids);
      }
    } catch (e) {
      console.error("Media upload error:", e);
    }
    return allIds;
  };

  const addCategory = (category) => {
    if (!categories.some(c => c.id === category.id)) {
      setCategories(prev => [...prev, category]);
      setCategoryInput("");
    }
  };

  const handleAddNewCategory = async (name) => {
    if (!name?.trim()) { setAddCategoryDialogOpen(true); return; }
    setCategoryCreating(true);
    try {
      const response = await UserService.addNewCategories({ name: name.trim() });
      if (response?.status === true) {
        const refreshed = await UserService.getAllCategories();
        if (refreshed?.status === true) {
          const created = refreshed.categories.find(
            c => c.name.toLowerCase() === name.trim().toLowerCase()
          );
          if (created) addCategory(created);
        }
        toast.success(`Category "${name}" created and added`);
      } else {
        toast.error(response?.message || "Failed to create category");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create category");
    } finally {
      setCategoryCreating(false);
      setAddCategoryDialogOpen(false);
      setNewCategoryDialogInput("");
    }
  };

  const resetForm = () => {
    setTopic(""); setContent(""); setCategories([]); setCategoryInput(""); setFeaturedUrl(""); setAllowEdit(false); setSelectedTopic("");
    setIsScheduled(false); setScheduleDate(getTodayDateString()); setScheduleTime(getCurrentTimeString());
    imageUrls.forEach(URL.revokeObjectURL);
    setImages([]); setImageUrls([]); setVideos([]); setAttachedFiles([]);
  };

  const buildPostPayload = (mediaIds) => {
    let scheduledTimeUtc = null;
    if (isScheduled && scheduleDate && scheduleTime) {
      const combinedDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      scheduledTimeUtc = combinedDateTime.toISOString().split(".")[0] + "Z";
    }

    return {
      content,
      categories: categories.map(c => c.id),
      mediaIds,
      libraryMediaIds: [],
      allowEdit,
      xGeneratedContent: null,
      taggedUserIds: [],
      featuredUrl: hasFeatured ? featuredUrl : null,
      scheduledTimeUtc: scheduledTimeUtc,
      variations: null,
      sourceType: "PERSONAL",
    };
  };

  const handlePost = async () => {
    if (!hasContent) { toast.error("Write content before posting"); return; }
    if (overLimit) { toast.error("Content exceeds LinkedIn limit"); return; }
    if (!anyPlatformSelected) { toast.error("Select at least one platform to share to"); return; }

    if (isScheduled) {
      if (!scheduleDate || !scheduleTime) {
        toast.error("Please select both Date and Time for scheduling");
        return;
      }
      const combinedDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      if (isNaN(combinedDateTime.getTime()) || combinedDateTime <= new Date()) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }

    try {
      setPosting(true);
      const mediaIds = await uploadAllMedia();

      if (isScheduled) {
        // Pass scheduledTimeUtc: null to createPost to prevent user-management from creating a duplicate INTERNAL schedule row
        const postPayload = buildPostPayload(mediaIds);
        const result = await createPost({ ...postPayload, scheduledTimeUtc: null, status: "SCHEDULED" });
        if (result?.status === true && result?.postMediaResponseData?.postId) {
          const dbPostId = result.postMediaResponseData.postId;
          const selectedPlatforms = Object.entries(platformEnabled)
            .filter(([_, enabled]) => enabled)
            .map(([key, _]) => key);

          const combinedDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
          const scheduledTimeUtc = combinedDateTime.toISOString().split(".")[0] + "Z";
          const targetPlatforms = selectedPlatforms.length > 0 ? selectedPlatforms : ["INTERNAL"];

          const schedulePayload = {
            postId: dbPostId,
            content: content,
            type: "POST",
            scheduledTimeUtc: scheduledTimeUtc,
            platforms: targetPlatforms,
          };
          await scheduleExternalPost(schedulePayload);
          toast.success("Post scheduled successfully!");
          resetForm();
        } else {
          toast.error(result?.message || "Failed to schedule post");
        }
        return;
      }

      const result = await createPost({ ...buildPostPayload(mediaIds), status: "EXTERNAL_ONLY" });

      if (result?.status !== true || !result?.postMediaResponseData?.postId) {
        toast.error("Failed to prepare post for external publication.");
        return;
      }

      const dbPostId = result.postMediaResponseData.postId;

      const apiMap = { LINKEDIN: createLinkedinExternalPost, X: createXExternalPost };
      const nameMap = { LINKEDIN: "Linkedin", X: "Twitter / X" };
      let anyShared = false;

      const cleanContent = isPayoneerDomain(businessPages) ? stripHashtagsFromText(content) : content;
      for (const [key, enabled] of Object.entries(platformEnabled)) {
        if (!enabled) continue;
        const payload = {
          postId: dbPostId,
          type: "POST",
          content: cleanContent,
        };
        try {
          await apiMap[key](payload);
          toast.success(`Shared to ${nameMap[key]}`);
          anyShared = true;
        } catch (err) {
          toast.error(`${nameMap[key]}: ${err.message || "Share failed"}`);
        }
      }

      if (anyShared) resetForm();
    } catch (err) { toast.error(err.message || "Post failed"); }
    finally { setPosting(false); }
  };

  const handleSaveEdit = async () => {
    if (!content.trim()) { toast.error("Content cannot be empty"); return; }
    try {
      setPosting(true);
      const mediaIds = await uploadAllMedia();
      const response = await updatePostContent(editPostId, { content, mediaIds: mediaIds.length > 0 ? mediaIds : undefined });
      if (response?.status) {
        toast.success("Post updated successfully");
        resetForm();
        onEditSuccess && onEditSuccess();
      } else {
        toast.error("Failed to update post");
      }
    } catch (err) { toast.error(err.message || "Update failed"); }
    finally { setPosting(false); }
  };

  const handleSaveDraft = async () => {
    if (!content.trim()) { toast.error("Nothing to save"); return; }
    try {
      setDraftSaving(true);
      const mediaIds = await uploadAllMedia();
      const result = await createPost({ ...buildPostPayload(mediaIds), status: "DRAFT" });
      if (result?.status === true) { toast.success("Saved as draft!"); resetForm(); }
      else toast.error(result?.message || "Draft save failed");
    } catch (err) { toast.error(err.message || "Draft save failed"); }
    finally { setDraftSaving(false); }
  };

  // ── Char counter class ───────────────────────────────────────────────
  const charClass = overLimit ? "error" : charCount > 2700 ? "warn" : "";

  return (
    <Box className="compose-page">

      {/* ══════════════════ LEFT: COMPOSE FORM ══════════════════ */}
      <Box className="compose-form-panel">

        {/* Header */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1.2, sm: 2 }, pb: { xs: 0.8, sm: 1.2 }, borderBottom: "1px solid #EDEFF2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: { xs: 15, sm: 16 }, color: "#3B3B3B" }}>
              {isEditMode ? "Edit Post" : "Compose Post"}
            </Typography>
            <Typography sx={{ fontFamily: "Poppins", fontSize: { xs: 11, sm: 12 }, color: "#95919D", mt: 0.2 }}>
              {isEditMode ? "Edit your post content using AI tools below" : "Dedicated workspace to write, refine, and preview your post"}
            </Typography>
          </Box>
          {isEditMode && onEditCancel && (
            <IconButton onClick={onEditCancel} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {/* Scrollable form body */}
        <Box className="compose-form-scroll">

          {/* Topic + Generate — hidden in edit mode */}
          {!isEditMode && <Box
            id="onboarding-compose-page-input"
            sx={{
              display: "flex",
              backgroundColor: "#F8F9FC",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              padding: "12px",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <TextField
              variant="standard"
              placeholder="Compose"
              multiline
              fullWidth
              value={topic}
              onChange={e => { setTopic(e.target.value); if (selectedTopic && e.target.value !== selectedTopic) setSelectedTopic(""); }}
              onKeyDown={e => e.key === "Enter" && handleGenerate()}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: "14px", color: "#7c7676", backgroundColor: "transparent", fontFamily: "Poppins" },
              }}
              sx={{
                flex: 1,
                minWidth: "200px",
                "& .MuiInputBase-root": { backgroundColor: "transparent" },
              }}
            />
            {aiLoading ? (
              <Button
                variant="contained"
                disabled
                sx={{
                  width: "132px",
                  background: "linear-gradient(to right, #BC77F4, #0047AB)",
                  color: "#fff !important",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13px",
                  padding: "5px 10px",
                  boxShadow: "none",
                  fontWeight: 500,
                  alignSelf: "flex-end",
                  cursor: "not-allowed !important",
                  pointerEvents: "auto !important",
                  "&.Mui-disabled": {
                    opacity: 0.65,
                    color: "#fff !important",
                    background: "linear-gradient(to right, #BC77F4, #0047AB)",
                    cursor: "not-allowed !important",
                    pointerEvents: "auto !important",
                  },
                }}
              >
                ✨ Generating{dots}
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  width: "132px",
                  background: "linear-gradient(to right, #BC77F4, #0047AB)",
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "13px",
                  textTransform: "none",
                  padding: "5px 10px",
                  boxShadow: "none",
                  fontWeight: 500,
                  alignSelf: "flex-end",

                  "&:hover": {
                    background: "linear-gradient(to right, #8a6be9, #306ddf)",
                    boxShadow: "none",
                  },
                }}
                onClick={handleGenerate}
              >
                ✨ Generate
              </Button>
            )}
          </Box>}

          {/* Trending Topics — matches Timeline/Postcreation style */}
          {!isEditMode && (orgTrendsData.length > 0 || personalTrendsData.length > 0) && (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "12px 16px",
                backgroundColor: "#F8F9FC",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body2" sx={{ color: "#95919D", fontWeight: 400, fontFamily: "Poppins" }}>
                  Trending Topics
                </Typography>
                <Select
                  size="small"
                  value={trendingTopicType}
                  onChange={(e) => setTrendingTopicType(e.target.value)}
                  sx={{ height: 24, fontSize: "12px", "& .MuiSelect-select": { py: 0, px: 1 } }}
                >
                  <MenuItem value="org" sx={{ fontSize: "12px" }}>Organization</MenuItem>
                  <MenuItem value="personal" sx={{ fontSize: "12px" }}>Personal</MenuItem>
                </Select>
              </Box>
              <Box sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: "6px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#d1d5db", borderRadius: "10px" },
                "&::-webkit-scrollbar-track": { backgroundColor: "#f3f4f6", borderRadius: "10px" },
              }}>
                {trendsData.length > 0 ? trendsData.map((t, i) => (
                  <Chip
                    key={i}
                    label={t}
                    variant="outlined"
                    onClick={() => handleTopicChipClick(t)}
                    sx={{
                      backgroundColor: selectedTopic === t ? "#d9e7ff" : "#eef2fb",
                      borderColor: selectedTopic === t ? "#0047AB" : "#73a3f3",
                      borderRadius: "24px",
                      fontSize: "11px",
                      fontWeight: selectedTopic === t ? 500 : 400,
                      padding: "3px 8px",
                      color: selectedTopic === t ? "#0047AB" : "#222",
                      height: "auto",
                      whiteSpace: "nowrap",
                      fontFamily: "Poppins",
                    }}
                  />
                )) : (
                  <Typography variant="body2" sx={{ color: "#95919D", fontSize: "12px" }}>
                    No {trendingTopicType === "org" ? "organization" : "personal"} topics configured.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* AI Refine */}
          <Box>
            <Typography variant="body2" sx={{ color: "#95919D", fontWeight: 400, mb: 0.8, fontFamily: "Poppins" }}>
              AI Refine
            </Typography>
            <Box className="compose-refine-row">
              {[
                { label: "Rewrite", action: "rewrite", icon: <EditNoteIcon sx={{ fontSize: 15 }} /> },
                { label: "Shorten", action: "shorten", icon: <ShortTextIcon sx={{ fontSize: 15 }} /> },
                { label: "Expand", action: "expand", icon: <UnfoldMoreIcon sx={{ fontSize: 15 }} /> },
              ].map(({ label, action, icon }) => (
                <Button
                  key={action}
                  size="small"
                  variant="outlined"
                  startIcon={refineLoading ? <CircularProgress size={12} color="inherit" /> : icon}
                  onClick={() => handleRefine(action)}
                  disabled={isLoading}
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderRadius: "8px",
                    textTransform: "none",
                    padding: "4px 12px",
                    borderColor: "#0047AB",
                    color: "#0047AB",
                    background: "#fff",
                    "&:hover": { background: "#F4F0FF", borderColor: "#0047AB" },
                    "&.Mui-disabled": { opacity: 0.5 },
                  }}
                >
                  {label}
                </Button>
              ))}

              <Button
                size="small"
                variant="outlined"
                startIcon={<RecordVoiceOverOutlinedIcon sx={{ fontSize: 15 }} />}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: 15 }} />}
                onClick={e => setToneAnchor(e.currentTarget)}
                disabled={isLoading}
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  padding: "4px 12px",
                  borderColor: "#0047AB",
                  color: "#0047AB",
                  background: "#fff",
                  "&:hover": { background: "#F4F0FF", borderColor: "#0047AB" },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                Change Tone
              </Button>
              <Menu anchorEl={toneAnchor} open={Boolean(toneAnchor)} onClose={() => setToneAnchor(null)}>
                {TONE_OPTIONS.map(tone => (
                  <MenuItem
                    key={tone}
                    onClick={() => { setToneAnchor(null); handleRefine("change_tone", tone); }}
                    sx={{ fontFamily: "Poppins", fontSize: 13 }}
                  >
                    {tone}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>

          {/* Content textarea */}
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <span className="compose-label" style={{ marginBottom: 0 }}>Content</span>
                <Tooltip title="Add emoji">
                  <IconButton
                    size="small"
                    onClick={e => setEmojiAnchor(e.currentTarget)}
                    sx={{ color: "#0047AB", padding: "2px" }}
                  >
                    <SentimentSatisfiedAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <span className={`compose-char-counter ${charClass}`} style={{ marginTop: 0 }}>
                {charCount.toLocaleString()} / {LINKEDIN_LIMIT.toLocaleString()}
              </span>
            </Box>
            <textarea
              ref={contentRef}
              className={`compose-textarea${overLimit ? " over-limit" : ""}`}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write your post here, or use Generate above to create content from a topic…"
              rows={6}
            />
          </Box>

          {/* Media upload */}
          <Box>
            <Typography variant="body2" sx={{ color: "#95919D", fontWeight: 400, mb: 0.8, fontFamily: "Poppins" }}>
              Media & Emojis
            </Typography>

            {/* Upload type buttons — Timeline SVG icon style */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Tooltip title="Add images (max 5)">
                <label>
                  <input type="file" accept="image/*" multiple hidden onChange={handleImageSelect} />
                  <IconButton component="span" color="primary" sx={{ padding: "8px 8px 8px 0px !important" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "#F4F0FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ImageOutlinedIcon sx={{ color: "#0047AB", fontSize: 18 }} />
                    </Box>
                  </IconButton>
                </label>
              </Tooltip>
              <Tooltip title="Add video">
                <label>
                  <input type="file" accept="video/*" multiple hidden
                    onChange={e => { setVideos(p => [...p, ...Array.from(e.target.files)].slice(0, 2)); e.target.value = ""; }} />
                  <IconButton component="span" color="primary" sx={{ padding: "8px 8px 8px 0px !important" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "#F4F0FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <VideocamOutlinedIcon sx={{ color: "#0047AB", fontSize: 18 }} />
                    </Box>
                  </IconButton>
                </label>
              </Tooltip>
              <Tooltip title="Attach document">
                <label>
                  <input type="file" accept=".pdf,.doc,.docx" multiple hidden
                    onChange={e => { setAttachedFiles(p => [...p, ...Array.from(e.target.files)].slice(0, 3)); e.target.value = ""; }} />
                  <IconButton component="span" color="primary" sx={{ padding: "8px 8px 8px 0px !important" }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "#F4F0FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <AttachFileIcon sx={{ color: "#0047AB", fontSize: 18 }} />
                    </Box>
                  </IconButton>
                </label>
              </Tooltip>
              <Tooltip title="Add emoji">
                <IconButton
                  component="span"
                  color="primary"
                  onClick={e => setEmojiAnchor(e.currentTarget)}
                  sx={{ padding: "8px 8px 8px 0px !important" }}
                >
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "#F4F0FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <SentimentSatisfiedAltIcon sx={{ color: "#0047AB", fontSize: 18 }} />
                  </Box>
                </IconButton>
              </Tooltip>
            </Box>

            {/* Images — Postcreation style */}
            {imageUrls.length > 0 && (
              <Box mt={1.5} className="img_container">
                <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#9C9C9C", mb: 1 }}>
                  Upload from Library : {imageUrls.length}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {/* + add more button */}
                  <label style={{ display: "inline-block", cursor: "pointer" }}>
                    <input type="file" accept="image/*" multiple hidden onChange={handleImageSelect} />
                    <IconButton component="span" className="AddIcon">
                      <AddIcon />
                    </IconButton>
                  </label>
                  {/* Image grid */}
                  {imageUrls.map((url, i) => (
                    <Box key={i} sx={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={url}
                        alt={`img-${i}`}
                        className="containerImage"
                        style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, display: "block" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(i)}
                        sx={{
                          position: "absolute", top: -6, right: -6,
                          bgcolor: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px",
                          width: 18, height: 18, zIndex: 2,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Videos — Postcreation style */}
            {videos.length > 0 && (
              <Box mt={1.5} className="img_container">
                <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#9C9C9C", mb: 1 }}>
                  Videos : {videos.length}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {/* + add more video */}
                  <label>
                    <input type="file" accept="video/*" multiple hidden
                      onChange={e => { setVideos(p => [...p, ...Array.from(e.target.files)].slice(0, 2)); e.target.value = ""; }} />
                    <IconButton component="span" className="AddIcon">
                      <AddIcon />
                    </IconButton>
                  </label>
                  {videos.map((v, i) => (
                    <Box key={i} sx={{ position: "relative" }}>
                      <video
                        src={URL.createObjectURL(v)}
                        className="containerImage"
                        style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setVideos(p => p.filter((_, j) => j !== i))}
                        sx={{
                          position: "absolute", top: -6, right: -6,
                          bgcolor: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px",
                          width: 18, height: 18,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Documents */}
            {attachedFiles.length > 0 && (
              <Box mt={1.5} className="img_container">
                <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#9C9C9C", mb: 1 }}>
                  Documents : {attachedFiles.length}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {attachedFiles.map((f, i) => (
                    <Chip key={i} label={f.name} size="small" icon={<AttachFileIcon sx={{ fontSize: 13 }} />}
                      onDelete={() => setAttachedFiles(p => p.filter((_, j) => j !== i))}
                      sx={{ fontFamily: "Poppins", fontSize: 11, background: "#E7F1FF", border: "0.6px solid #2D76DC" }} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Categories section — temporarily hidden
          <Box>
            <span className="compose-label">Categories</span>
            <Autocomplete
              multiple
              options={(categoriesData || []).filter(cat => !categories.some(c => c.id === cat.id))}
              value={categories}
              inputValue={categoryInput}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== "reset") setCategoryInput(newInputValue);
              }}
              onChange={(event, newValues, reason, details) => {
                const selected = details?.option;
                if (selected?.__isFeatured) {
                  const existing = (categoriesData || []).find(c => c.name?.toLowerCase() === "featured");
                  if (existing) addCategory(existing);
                  else handleAddNewCategory("Featured");
                  return;
                }
                if (selected?.__isAddNew) {
                  handleAddNewCategory(selected.name);
                  return;
                }
                const valid = newValues.filter(v => !v?.__isAddNew && !v?.__isFeatured);
                setCategories(valid);
                setCategoryInput("");
              }}
              filterOptions={(options, params) => {
                const alreadyFeatured = categories.some(c => c.name?.toLowerCase() === "featured");
                const nonFeatured = options.filter(opt => opt.name?.toLowerCase() !== "featured");
                const filtered = nonFeatured.filter(opt =>
                  opt.name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                const label = params.inputValue.trim();
                const alreadyExists = nonFeatured.some(opt => opt.name?.toLowerCase() === label.toLowerCase());
                if (!alreadyExists) filtered.push({ __isAddNew: true, name: label });
                if (!alreadyFeatured) filtered.unshift({ __isFeatured: true, name: "Featured" });
                return filtered;
              }}
              getOptionLabel={option => typeof option === "string" ? option : option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id || index}
                    label={option.name}
                    size="small"
                    deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                    sx={{ fontFamily: "Poppins", fontSize: 11, background: "#E7F1FF", border: "0.6px solid #2D76DC" }}
                  />
                ))
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.__isFeatured ? "featured-pin" : option.__isAddNew ? "add-new" : option.id}
                >
                  {option.__isFeatured ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <StarIcon fontSize="small" sx={{ color: "inherit" }} />
                      <Typography fontSize="13px" sx={{ color: "inherit", fontWeight: 600 }}>Featured</Typography>
                    </Box>
                  ) : option.__isAddNew ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon fontSize="small" sx={{ color: "inherit" }} />
                      <Typography fontSize="13px" sx={{ color: "inherit", fontWeight: 500 }}>
                        {option.name ? `Add "${option.name}"` : "Add New Category"}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography fontSize="13px" sx={{ color: "inherit" }}>{option.name}</Typography>
                  )}
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={categories.length === 0 ? "Select or add categories" : ""}
                  size="small"
                  sx={{
                    mt: 0.5,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px", flexWrap: "wrap" },
                  }}
                />
              )}
              PopperComponent={props => (
                <Popper
                  {...props}
                  sx={{
                    padding: "8px",
                    "& .MuiAutocomplete-listbox": {
                      padding: "5px",
                      border: "1px solid #DEDFE5",
                      borderRadius: "8px",
                      "& li": {
                        backgroundColor: "#FFFFFF",
                        padding: "8px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        color: "#3B3B3B",
                        transition: "background-color 0.2s ease, color 0.2s ease",
                      },
                      "& li:hover": { backgroundColor: "#0047AB", color: "#FFFFFF" },
                      "& li:first-child": { color: "#B8860B", borderBottom: "1px solid #f0f0f0", marginBottom: "4px" },
                      "& li:first-child:hover": { backgroundColor: "#FFF8E1", color: "#B8860B" },
                      "& li:last-child": { color: "#0047AB", borderTop: "1px solid #f0f0f0", marginTop: "4px" },
                      "& li:last-child:hover": { backgroundColor: "#0047AB", color: "#FFFFFF" },
                    },
                  }}
                />
              )}
              loading={categoryCreating}
              disableClearable
            />
            {hasFeatured && (
              <Box mt={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={featuredUrl}
                  onChange={e => setFeaturedUrl(e.target.value)}
                  placeholder="Paste featured article or website URL"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" sx={{ color: "#B8860B" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: "13px",
                      "& fieldset": { borderColor: "#FFD700" },
                      "&:hover fieldset": { borderColor: "#B8860B" },
                      "&.Mui-focused fieldset": { borderColor: "#B8860B" },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
          */}


          {/* ── Share Platforms ─────────────────────────────────── */}
          {!isEditMode && <Box>
            <span className="compose-label">Share To</span>
            <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
              {PLATFORMS.map(p => {
                const connected = isConnected(p.key);
                const selected = platformEnabled[p.key];
                return (
                  <Tooltip key={p.key} title={!connected ? `${p.name} — not connected` : ""}>
                    <Box
                      onClick={() => {
                        if (!connected) { setConnectDialog(p.name); return; }
                        setPlatformEnabled(prev => ({ ...prev, [p.key]: !prev[p.key] }));
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 0.8,
                        borderRadius: "10px",
                        cursor: "pointer",
                        border: selected ? "2px solid #0047AB" : "2px solid #E0E0E0",
                        backgroundColor: selected ? "#E7F1FF" : "#F8F8F8",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: "#0047AB", backgroundColor: "#dceeff" },
                      }}
                    >
                      <Avatar src={p.icon} alt={p.name} sx={{ width: 22, height: 22, bgcolor: "#fff" }} />
                      <Typography sx={{ fontFamily: "Poppins", fontSize: 13, fontWeight: selected ? 600 : 400, color: selected ? "#0047AB" : "#5C5B5D" }}>
                        {p.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>}

          {/* ── Schedule Post Section ────────────────────────── */}
          {!isEditMode && (
            <Box sx={{ mt: 2, mb: 1, p: 1.5, borderRadius: "10px", backgroundColor: isScheduled ? "#F0F4FF" : "#F8F8F8", border: isScheduled ? "1px solid #0047AB" : "1px solid #E0E0E0", transition: "all 0.2s ease" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isScheduled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsScheduled(checked);
                      if (checked) {
                        const today = getTodayDateString();
                        const nowTime = getCurrentTimeString();
                        if (!scheduleDate || scheduleDate < today) setScheduleDate(today);
                        if (!scheduleTime || ((scheduleDate || today) === today && scheduleTime < nowTime)) {
                          setScheduleTime(nowTime);
                        }
                      }
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 600, color: isScheduled ? "#0047AB" : "#3B3B3B" }}>
                    Schedule Post for Later
                  </Typography>
                }
              />

              {isScheduled && (
                <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "nowrap", alignItems: "center", width: "100%" }}>
                  <TextField
                    type="date"
                    label="Date"
                    size="small"
                    value={scheduleDate}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setScheduleDate(newDate);
                      const today = getTodayDateString();
                      const nowTime = getCurrentTimeString();
                      if (newDate === today && scheduleTime < nowTime) {
                        setScheduleTime(nowTime);
                      }
                    }}
                    inputProps={{ min: getTodayDateString() }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, backgroundColor: "#FFF" },
                      "& .MuiInputBase-input": { px: 1, py: 1, fontSize: 12 }
                    }}
                  />
                  <TextField
                    type="time"
                    label="Time"
                    size="small"
                    value={scheduleTime}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      const today = getTodayDateString();
                      const nowTime = getCurrentTimeString();
                      if (scheduleDate === today && newTime < nowTime) {
                        setScheduleTime(nowTime);
                      } else {
                        setScheduleTime(newTime);
                      }
                    }}
                    inputProps={{ min: scheduleDate === getTodayDateString() ? getCurrentTimeString() : undefined }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, backgroundColor: "#FFF" },
                      "& .MuiInputBase-input": { px: 1, py: 1, fontSize: 12 }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          <hr className="compose-divider" />

          {/* Action buttons */}
          <Box className="compose-actions">
            {isEditMode ? (
              <>
                {onEditCancel && (
                  <Button onClick={onEditCancel} disabled={isLoading} sx={{ fontFamily: "Poppins", textTransform: "none", backgroundColor: "#F8F6FD", color: "#003db3", borderRadius: "8px", px: 3 }}>
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSaveEdit}
                  disabled={isLoading || overLimit || !hasContent}
                  className="btn-post"
                >
                  {posting && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}
                  Save Changes
                </Button>
              </>
            ) : (
              <Button
                onClick={handlePost}
                disabled={isLoading || overLimit || !hasContent || !anyPlatformSelected || (isScheduled && (!scheduleDate || !scheduleTime))}
                className="btn-post"
              >
                {posting && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}
                {isScheduled ? "✨ Schedule Post" : "Post Now"}
              </Button>
            )}
          </Box>

        </Box>
      </Box>

      {/* ══════════════════ RIGHT: LIVE PREVIEW ══════════════════ */}
      <PostPreview content={content} images={images} videos={videos} documents={attachedFiles} userProfile={userProfile} businessPages={businessPages} />

      {/* ── Account Not Connected dialog ─────────────────────── */}
      <Dialog
        open={!!connectDialog}
        onClose={() => setConnectDialog(null)}
        PaperProps={{ sx: { borderRadius: "16px", padding: "16px 20px", maxWidth: "420px", width: "100%" } }}
      >
        <IconButton onClick={() => setConnectDialog(null)} sx={{ position: "absolute", top: 16, right: 16 }}>
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ fontWeight: 500, color: "#484848", fontSize: "20px", p: 0, mb: 1 }}>
          Account Not Connected
        </DialogTitle>
        <DialogContent sx={{ padding: "8px 0px 0px" }}>
          <Typography sx={{ fontSize: "16px", color: "#616161", mb: 3 }}>
            Please connect your {connectDialog} profile in case you want to share.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              onClick={() => setConnectDialog(null)}
              sx={{
                backgroundColor: "#F4F0FF", color: "#0D47A1", textTransform: "none",
                fontSize: "16px", fontWeight: 500, borderRadius: "8px", px: 4, py: 1,
                "&:hover": { backgroundColor: "#E9D5FF" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConnectDialog(null);
                const basePath = isAdmin ? "/admin" : "/employees";
                navigate(`${basePath}/settings`, { state: { openPersonalAccounts: true } });
              }}
              sx={{
                backgroundColor: "#003db3", color: "#fff", textTransform: "none",
                fontSize: "16px", fontWeight: 500, borderRadius: "8px", px: 4, py: 1,
                "&:hover": { backgroundColor: "#002f8c" },
              }}
            >
              Connect Account
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Add New Category dialog ─────────────────────── */}
      <Dialog
        open={addCategoryDialogOpen}
        onClose={() => { setAddCategoryDialogOpen(false); setNewCategoryDialogInput(""); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", padding: "8px" } }}
      >
        <DialogTitle sx={{ fontWeight: 500, fontSize: "18px" }}>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Category name"
            value={newCategoryDialogInput}
            onChange={e => setNewCategoryDialogInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleAddNewCategory(newCategoryDialogInput); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => { setAddCategoryDialogOpen(false); setNewCategoryDialogInput(""); }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleAddNewCategory(newCategoryDialogInput)}
            variant="contained"
            disabled={!newCategoryDialogInput.trim() || categoryCreating}
            sx={{ backgroundColor: "#0047AB", borderRadius: "8px", textTransform: "none" }}
          >
            {categoryCreating ? "Adding..." : "Add"}
          </Button>
        </Box>
      </Dialog>

      {/* ── Tea Loading Modal for AI Generation ─────────────────────── */}
      <TeaLoadingModal
        open={showGeneratingPopup}
        onClose={() => setShowGeneratingPopup(false)}
        message={"We're working on your post — till then,\nhave a cup of tea."}
      />

      {/* ── Emoji Picker Popover ─────────────────────── */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }
        }}
      >
        <EmojiPicker
          onEmojiClick={handleEmojiClickCompose}
          lazyLoadEmojis
          height={380}
          width={320}
          skinTonesDisabled
        />
      </Popover>

    </Box>
  );
};

export default ComposePage;
